import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";

function NavIcon({ children, active }) {
  return (
    <span
      className={`flex h-7 w-7 items-center justify-center rounded-xl transition ${
        active ? "bg-amber-400/25 text-amber-700" : "text-slate-500"
      }`}
    >
      {children}
    </span>
  );
}

function HomeIcon({ active }) {
  return (
    <NavIcon active={active}>
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M3 10.5 12 3l9 7.5V20a1 1 0 0 1-1 1h-5v-6H9v6H4a1 1 0 0 1-1-1v-9.5z" strokeLinejoin="round" />
      </svg>
    </NavIcon>
  );
}

function ExploreIcon({ active }) {
  return (
    <NavIcon active={active}>
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <rect x="3" y="3" width="7" height="7" rx="1.5" />
        <rect x="14" y="3" width="7" height="7" rx="1.5" />
        <rect x="3" y="14" width="7" height="7" rx="1.5" />
        <rect x="14" y="14" width="7" height="7" rx="1.5" />
      </svg>
    </NavIcon>
  );
}

function TicketIcon({ active }) {
  return (
    <NavIcon active={active}>
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <path d="M4 8h16v8H4z" />
        <path d="M8 8v8M16 8v8" />
      </svg>
    </NavIcon>
  );
}

function HeartIcon({ active }) {
  return (
    <NavIcon active={active}>
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5"
        fill={active ? "currentColor" : "none"}
        stroke="currentColor"
        strokeWidth="2"
      >
        <path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" />
      </svg>
    </NavIcon>
  );
}

function ProfileIcon({ active }) {
  return (
    <NavIcon active={active}>
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
        <circle cx="12" cy="8" r="3.5" />
        <path d="M5 20c0-3.3 3.1-5 7-5s7 1.7 7 5" strokeLinecap="round" />
      </svg>
    </NavIcon>
  );
}

const NAV_ITEMS = [
  { key: "home", label: "Home", to: "/", icon: HomeIcon, match: (p) => p === "/" },
  {
    key: "explore",
    label: "Explore",
    to: "/featured",
    icon: ExploreIcon,
    match: (p) =>
      p.startsWith("/featured") ||
      p.startsWith("/trending") ||
      p.startsWith("/weekend"),
  },
  {
    key: "tickets",
    label: "Passes",
    to: "/tickets",
    icon: TicketIcon,
    match: (p) => p === "/tickets" || p.startsWith("/my-bookings"),
    requireAuth: true,
  },
  {
    key: "saved",
    label: "Saved",
    to: "/wishlist",
    icon: HeartIcon,
    match: (p) => p === "/wishlist",
    requireAuth: true,
  },
  {
    key: "profile",
    label: "Profile",
    to: "/profile",
    icon: ProfileIcon,
    match: (p) => p === "/profile" || p === "/settings",
    requireAuth: true,
  },
];

export default function MobileBottomNav() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const auth = useAuth?.() ?? {};
  const user = auth?.user ?? null;

  const go = (item, e) => {
    if (item.requireAuth && !user?.token) {
      e.preventDefault();
      navigate("/login", { state: { from: item.to } });
      return;
    }
  };

  return (
    <nav
      className="fixed inset-x-0 bottom-0 z-50 md:hidden"
      aria-label="Mobile navigation"
    >
      <div className="pointer-events-none absolute inset-x-0 -top-6 h-6 bg-gradient-to-t from-white/95 to-transparent" aria-hidden />

      <div
        className="border-t border-amber-100/80 bg-white/95 shadow-[0_-8px_32px_rgba(15,23,42,0.08)] backdrop-blur-md"
        style={{ paddingBottom: "max(0.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto flex max-w-lg items-stretch justify-around px-1 pt-1.5">
          {NAV_ITEMS.map((item) => {
            const active = item.match(pathname);
            const Icon = item.icon;
            const labelClass = active
              ? "font-bold text-amber-700"
              : "font-medium text-slate-500";

            return (
              <Link
                key={item.key}
                to={item.to}
                onClick={(e) => go(item, e)}
                className="flex min-w-0 flex-1 flex-col items-center gap-0.5 px-1 py-1 no-underline"
                aria-current={active ? "page" : undefined}
              >
                <Icon active={active} />
                <span className={`text-[10px] leading-tight ${labelClass}`}>
                  {item.label}
                </span>
                {active && (
                  <span className="mt-0.5 h-0.5 w-5 rounded-full bg-amber-500" />
                )}
              </Link>
            );
          })}
        </div>
      </div>
    </nav>
  );
}
